import { cn } from "../lib/cn";
import { CARD_VARIANT_CLASS, CARD_VARIANT_STYLE } from "../primitives/card";

/**
 * Stable bar geometry for the skeleton waveform: 48 bars with a sine-
 * envelope height profile. Each bar carries an explicit `id` so the
 * React key is identity-bound, not position-bound.
 */
const SKELETON_WAVEFORM_BARS: ReadonlyArray<{ id: string; height: number }> = Array.from(
  { length: 48 },
  (_, i) => ({
    id: `skeleton-bar-${i}`,
    height: Math.max(18, Math.round(Math.sin((i / 48) * Math.PI) * 24 + 10)),
  }),
);

/**
 * BriefSurfaceSkeleton, layout-matched placeholder for the hero brief card.
 *
 * Follows the 2026 skeleton convention: same rounded geometry, same region
 * sizes and same separators as the final render, so the transition reads
 * like content arriving rather than a UI replacement. A single slow pulse
 * (2s ease-in-out) carries across the whole card; no per-element shimmer.
 */
export function BriefSurfaceSkeleton({ className }: { className?: string }) {
  return (
    <article
      aria-hidden="true"
      className={cn(CARD_VARIANT_CLASS.brief, "skeleton-sheen text-teal-900", className)}
      style={CARD_VARIANT_STYLE.brief}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-7 pt-6 pb-5">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="h-[11px] w-[180px] rounded-full bg-teal-900/10" />
          <div className="h-[30px] w-[60%] rounded-md bg-teal-900/10" />
          <div className="h-[14px] w-[80%] rounded-md bg-teal-900/10" />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <div className="h-[11px] w-[110px] rounded-full bg-teal-900/10" />
          <div className="h-[11px] w-[140px] rounded-full bg-teal-900/10" />
        </div>
      </div>

      {/* Body: host block + narrative lines */}
      <div className="border-t border-teal-900/10 px-7 py-6">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[128px_1fr] md:gap-6">
          <div className="space-y-2">
            <div className="h-[112px] w-[112px] rounded-xl bg-teal-900/10" />
            <div className="space-y-1.5 pt-1">
              <div className="h-[11px] w-[86px] rounded-full bg-teal-900/10" />
              <div className="h-[10px] w-[120px] rounded-full bg-teal-900/10" />
              <div className="h-[10px] w-[88px] rounded-full bg-teal-900/10" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-[14px] w-[95%] rounded-md bg-teal-900/10" />
            <div className="h-[14px] w-[88%] rounded-md bg-teal-900/10" />
            <div className="h-[14px] w-[70%] rounded-md bg-teal-900/10" />
            <div className="mt-3 h-[12px] w-[90px] rounded-full bg-teal-900/10" />
          </div>
        </div>
      </div>

      {/* Audio player */}
      <div className="flex items-center gap-4 border-t border-teal-900/10 bg-teal-900/[0.03] px-6 py-4">
        <div className="flex items-center gap-1.5">
          <div className="h-9 w-9 rounded-full bg-teal-900/10" />
          <div className="h-[52px] w-[52px] rounded-full bg-teal-600/40" />
          <div className="h-9 w-9 rounded-full bg-teal-900/10" />
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="h-[12px] w-[220px] rounded-full bg-teal-900/10" />
          <div className="flex items-center gap-[2px]">
            {SKELETON_WAVEFORM_BARS.map((bar) => (
              <span
                key={bar.id}
                className="block min-w-[2px] flex-1 rounded-sm bg-teal-900/10"
                style={{ height: `${bar.height}px` }}
              />
            ))}
          </div>
        </div>
        <div className="h-[11px] w-[70px] rounded-full bg-teal-900/10" />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-teal-900/10 px-7 py-4">
        <div className="h-[12px] w-[220px] rounded-full bg-teal-900/10" />
        <div className="flex items-center gap-2">
          <div className="h-[22px] w-[110px] rounded-full bg-teal-900/10" />
          <div className="h-[22px] w-[70px] rounded-full bg-teal-900/10" />
          <div className="h-[22px] w-[70px] rounded-full bg-teal-900/10" />
        </div>
      </div>
    </article>
  );
}
